/**
 * Configuração do alerta sonoro de briefings pendentes pra secretária.
 *
 * Persistido em `clinics/{clinicId}/settings/secretaryAudioAlert`.
 * Quando o doc ainda não existe na clínica, retornamos o default em
 * memória (30s) sem precisar criar nada — assim instalações novas
 * funcionam sem migration.
 *
 * Lido pelo componente `BriefingAlertSound` (Shell) pra decidir a cada
 * quantos segundos repete o beep enquanto há briefings com
 * `secretaryBriefing.briefed=false`.
 *
 * Não armazena estado de "mute" individual — é só o intervalo em
 * segundos, valendo pra todos os usuários que ouvem o alerta na clínica.
 */
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from './config';

export interface SecretaryAudioAlertConfig {
  /** Intervalo em segundos entre repetições do alerta sonoro. */
  alertIntervalSeconds: number;
}

export const DEFAULT_SECRETARY_AUDIO_ALERT: SecretaryAudioAlertConfig = {
  alertIntervalSeconds: 30,
};

/** Mínimo aceitável pra evitar martelar a secretária. */
export const MIN_ALERT_INTERVAL_SECONDS = 10;
/** Máximo razoável — acima disso faz mais sentido desligar o alerta. */
export const MAX_ALERT_INTERVAL_SECONDS = 600;

function clampInterval(value: number, fallback: number): number {
  if (!Number.isFinite(value)) return fallback;
  const int = Math.round(value);
  if (int < MIN_ALERT_INTERVAL_SECONDS) return MIN_ALERT_INTERVAL_SECONDS;
  if (int > MAX_ALERT_INTERVAL_SECONDS) return MAX_ALERT_INTERVAL_SECONDS;
  return int;
}

let cached: { clinicId: string; value: SecretaryAudioAlertConfig } | null = null;

export const SecretaryAudioAlertService = {
  async load(clinicId: string): Promise<SecretaryAudioAlertConfig> {
    if (cached && cached.clinicId === clinicId) return cached.value;
    try {
      const snap = await getDoc(
        doc(db, 'clinics', clinicId, 'settings', 'secretaryAudioAlert'),
      );
      if (snap.exists()) {
        const raw = snap.data() as Partial<SecretaryAudioAlertConfig>;
        const value: SecretaryAudioAlertConfig = {
          alertIntervalSeconds: clampInterval(
            Number(raw.alertIntervalSeconds),
            DEFAULT_SECRETARY_AUDIO_ALERT.alertIntervalSeconds,
          ),
        };
        cached = { clinicId, value };
        return value;
      }
    } catch (err) {
      console.error('[secretary-audio-alert] load failed:', err);
    }
    cached = { clinicId, value: DEFAULT_SECRETARY_AUDIO_ALERT };
    return DEFAULT_SECRETARY_AUDIO_ALERT;
  },

  async save(clinicId: string, value: SecretaryAudioAlertConfig): Promise<void> {
    const normalized: SecretaryAudioAlertConfig = {
      alertIntervalSeconds: clampInterval(
        value.alertIntervalSeconds,
        DEFAULT_SECRETARY_AUDIO_ALERT.alertIntervalSeconds,
      ),
    };
    await setDoc(
      doc(db, 'clinics', clinicId, 'settings', 'secretaryAudioAlert'),
      normalized,
      { merge: true },
    );
    cached = { clinicId, value: normalized };
  },

  invalidateCache(): void {
    cached = null;
  },
};
